import { useProgress } from '@/stores/progress';
import { useRetrieval } from '@/stores/retrieval';
import { DAILY_CARD_CAP, useCardsRemainingToday } from '@/stores/spark';
import { dueArtifactIds } from '@disha/selection';
import { useMemo } from 'react';

export interface NavTabState {
  subtitle: string;
  emphasized: boolean;
}

export interface NavState {
  spark: NavTabState;
  today: NavTabState;
  notes: NavTabState;
  tutor: NavTabState;
}

/**
 * Derives per-tab subtitle copy from live store state. Hybrid voice:
 * default = purpose hint ("browse", "your plan"); when state exists,
 * surface it instead ("2 to review", "5 of 20 today"). Cheap enough to
 * call from the always-mounted nav — no network reads.
 */
export function useNavState(): NavState {
  const cardsRemaining = useCardsRemainingToday();
  const retrievalStates = useRetrieval((s) => s.states);
  const visits = useProgress((s) => s.visits);

  return useMemo(() => {
    const dueCount = dueArtifactIds(retrievalStates, Date.now()).length;
    const inProgress = Object.keys(visits).length;
    const sparkUsed = DAILY_CARD_CAP - cardsRemaining;

    return {
      spark:
        cardsRemaining === 0
          ? { subtitle: 'back at midnight', emphasized: false }
          : sparkUsed > 0
            ? { subtitle: `${cardsRemaining} of ${DAILY_CARD_CAP} left`, emphasized: false }
            : { subtitle: 'browse', emphasized: false },
      today:
        dueCount > 0
          ? { subtitle: `${dueCount} to review`, emphasized: true }
          : { subtitle: 'your plan', emphasized: false },
      notes:
        inProgress > 0
          ? { subtitle: `${inProgress} in progress`, emphasized: false }
          : { subtitle: 'catalog', emphasized: false },
      tutor: { subtitle: 'ask doubts', emphasized: false },
    };
  }, [cardsRemaining, retrievalStates, visits]);
}
